// CMS Users Page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Search, ChevronLeft, ChevronRight, User, Mail, Calendar, 
  MapPin, Ticket, Ban, CheckCircle, Eye, Download, RefreshCw, Shield, UserCog, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSUsers = () => {
  const { getAuthHeaders, hasRole } = useCMS();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [hasPassFilter, setHasPassFilter] = useState('');
  const [passTypeFilter, setPassTypeFilter] = useState('');
  const [suspendedFilter, setSuspendedFilter] = useState('');
  
  // Role edit dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editRole, setEditRole] = useState('read_only');
  const [savingRole, setSavingRole] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const roleColors = {
    super_admin: 'bg-red-500/20 text-red-400',
    support: 'bg-blue-500/20 text-blue-400',
    finance: 'bg-green-500/20 text-green-400',
    account_manager: 'bg-purple-500/20 text-purple-400',
    read_only: 'bg-slate-500/20 text-slate-400'
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    support: 'Support',
    finance: 'Finance',
    account_manager: 'Account Manager',
    read_only: 'Read Only'
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setEditIsAdmin(user.is_admin || false);
    setEditRole(user.admin_role || 'read_only');
    setRoleDialogOpen(true);
  };

  const saveUserRole = async () => {
    if (!selectedUser) return;
    setSavingRole(true);
    try {
      // Use mongo_id if available, otherwise user_id
      const userId = selectedUser.mongo_id || selectedUser.user_id;
      await axios.patch(
        `${API}/cms/users/${userId}/role`,
        { is_admin: editIsAdmin, role: editIsAdmin ? editRole : null },
        { headers: getAuthHeaders() }
      );
      toast.success('User role updated successfully');
      setRoleDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (search) params.append('search', search);
      if (hasPassFilter) params.append('has_pass', hasPassFilter);
      if (passTypeFilter) params.append('pass_type', passTypeFilter);
      if (suspendedFilter) params.append('is_suspended', suspendedFilter);

      const res = await axios.get(`${API}/cms/users?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setUsers(res.data.users);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, hasPassFilter, passTypeFilter, suspendedFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleSuspend = async (userId, suspend) => {
    const reason = suspend ? prompt('Enter suspension reason:') : null;
    if (suspend && !reason) return;

    try {
      const endpoint = suspend 
        ? `${API}/cms/users/${userId}/suspend?reason=${encodeURIComponent(reason)}`
        : `${API}/cms/users/${userId}/reactivate`;
      
      await axios.post(endpoint, {}, { headers: getAuthHeaders() });
      toast.success(suspend ? 'User suspended' : 'User reactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleExport = async (userId) => {
    try {
      const res = await axios.get(`${API}/cms/users/${userId}/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${userId}_export.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/cms/users/${userToDelete.user_id}`, {
        headers: getAuthHeaders()
      });
      toast.success(`User ${userToDelete.email} deleted`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-slate-400">{total} total users</p>
        </div>
        <Button variant="outline" onClick={fetchUsers} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by email, name, or ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600"
                />
              </div>
            </div>
            <Select value={hasPassFilter || "all"} onValueChange={(v) => setHasPassFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Has Pass" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="true">Has Pass</SelectItem>
                <SelectItem value="false">No Pass</SelectItem>
              </SelectContent>
            </Select>
            <Select value={passTypeFilter || "all"} onValueChange={(v) => setPassTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Pass Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Type</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="one_time">One-Time</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
            <Select value={suspendedFilter || "all"} onValueChange={(v) => setSuspendedFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="false">Active</SelectItem>
                <SelectItem value="true">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-700 bg-slate-900/50">
                <tr>
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Pass</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Role</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Country</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Registered</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        {user.pass_type ? (
                          <Badge variant={user.pass_type === 'annual' ? 'default' : 'secondary'}>
                            <Ticket className="w-3 h-3 mr-1" />
                            {user.pass_type}
                          </Badge>
                        ) : (
                          <span className="text-slate-500">No pass</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.is_admin ? (
                          <Badge className={roleColors[user.admin_role] || roleColors.read_only}>
                            <Shield className="w-3 h-3 mr-1" />
                            {roleLabels[user.admin_role] || 'Admin'}
                          </Badge>
                        ) : (
                          <span className="text-slate-500">User</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-slate-400">
                          <MapPin className="w-4 h-4" />
                          {user.country || 'N/A'}
                        </div>
                      </td>
                      <td className="p-4 text-slate-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="p-4">
                        {user.is_suspended ? (
                          <Badge variant="destructive">Suspended</Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-400 border-green-400/50">Active</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/cms/users/${user.user_id}`}>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          {hasRole('super_admin') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRoleDialog(user)}
                              title="Edit Role"
                            >
                              <UserCog className="w-4 h-4 text-purple-400" />
                            </Button>
                          )}
                          {hasRole('super_admin', 'support') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSuspend(user.user_id, !user.is_suspended)}
                            >
                              {user.is_suspended ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Ban className="w-4 h-4 text-red-400" />
                              )}
                            </Button>
                          )}
                          {hasRole('super_admin') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExport(user.user_id)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {hasRole('super_admin') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Edit Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                  <p className="text-sm text-slate-400">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div>
                  <Label className="font-medium">Admin Access</Label>
                  <p className="text-sm text-slate-400">Grant CMS admin privileges</p>
                </div>
                <Switch
                  checked={editIsAdmin}
                  onCheckedChange={setEditIsAdmin}
                />
              </div>

              {editIsAdmin && (
                <div className="space-y-2">
                  <Label>Admin Role</Label>
                  <Select value={editRole} onValueChange={setEditRole}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="account_manager">Account Manager</SelectItem>
                      <SelectItem value="read_only">Read Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUserRole} disabled={savingRole}>
              {savingRole ? 'Saving...' : 'Save Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete User</DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium">{userToDelete.first_name} {userToDelete.last_name}</p>
                  <p className="text-sm text-slate-400">{userToDelete.email}</p>
                </div>
              </div>
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-300">
                  This will permanently delete this user account and all related data including favorites, sessions, referrals, and newsletter subscription. This action cannot be undone.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-user-btn">
              {deleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSUsers;
